import { useQueryClient } from '@tanstack/react-query'
import { useMutation } from 'convex/react'
import { api } from '@backend/_generated/api'
import type { Id } from '@backend/_generated/dataModel'

import { DIRECTREPORTS_QUERY_KEYS } from '../types'
import type {
  InviteDirectReportRequest,
  InviteDirectReportResponse,
  PromoteUserResponse,
} from '../types'
import { useMyConvexOrganizationId, useMyConvexUserId } from '@/features/auth'
import { TRAJECTORY_PATHS_QUERY_KEYS } from '@/features/trajectory-paths'

export const useInviteDirectReport = () => {
  const convexOrgId = useMyConvexOrganizationId()
  const convexUserId = useMyConvexUserId()
  const queryClient = useQueryClient()
  const fn = useMutation(api.invites.createInvite)

  return {
    mutateAsync: async (
      data: InviteDirectReportRequest,
    ): Promise<InviteDirectReportResponse> => {
      const result = await fn({
        email: data.email,
        managerId: (data.managerId || convexUserId) as Id<'users'>,
        positionId: data.positionId as Id<'positions'>,
        roles: data.roles,
        path: data.path,
        organizationId: convexOrgId as Id<'organizations'>,
        siteUrl: window.location.origin,
      })

      await queryClient.invalidateQueries({
        queryKey: DIRECTREPORTS_QUERY_KEYS.infinite,
      })

      return {
        id: result.id,
        email: result.email,
        role: result.roles[0] ?? '',
        status: result.status,
        createdAt: result.createdAt,
        updatedAt: result.createdAt,
      }
    },
  }
}

/**
 * Mutation for promoting a user to a new position.
 * Uses the Convex promoteUser mutation (replaces Hono POST /promote).
 */
export const usePromoteUser = () => {
  const queryClient = useQueryClient()
  const fn = useMutation(api.positions.promoteUser)

  return {
    mutateAsync: async ({
      userId,
      positionId,
    }: {
      userId: string
      positionId: string
    }): Promise<PromoteUserResponse> => {
      const result = await fn({
        employeeUserId: userId as Id<'users'>,
        positionId: positionId as Id<'positions'>,
      })

      // Invalidate any remaining React Query caches that depend on user positions
      queryClient.invalidateQueries({
        queryKey: DIRECTREPORTS_QUERY_KEYS.infinite,
      })
      queryClient.invalidateQueries({
        queryKey: TRAJECTORY_PATHS_QUERY_KEYS.all,
      })

      return { updated: result.userPositionId } as PromoteUserResponse
    },
  }
}
